/**
 * Tiny TEA runtime: Msg → pure update → Cmd[].
 *
 * Domain code owns Model / Msg / Cmd / update. This module only runs the loop.
 * Prefer `useTea` in Vue components (holds `model`). Prefer `dispatch` in Pinia
 * when the model lives elsewhere (e.g. session) for a spike. See fp-conventions.
 */
import { runCmds } from "./runCmds";

export type UpdateFn<Model, Msg, Cmd> = (
  model: Model,
  msg: Msg,
) => readonly [Model, readonly Cmd[]];

/** Perform one effect description. Keep business rules out of here. */
export type RunCmd<Cmd> = (cmd: Cmd) => void | Promise<void>;

/**
 * Apply `update`, then run each returned Cmd in order.
 * Returns the next model (caller decides whether to hold it).
 * For Vue-owned model, use `useTea` so the ref updates before Cmds run.
 */
export async function dispatch<Model, Msg, Cmd>(
  model: Model,
  msg: Msg,
  update: UpdateFn<Model, Msg, Cmd>,
  runCmd: RunCmd<Cmd>,
): Promise<Model> {
  const [next, cmds] = update(model, msg);
  await runCmds(cmds, runCmd);
  return next;
}
